import { useEffect } from 'react';
import { Modal, Stack, TextInput, Button, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { coachesApi } from '../../../services/api/coaches';
import type { Coach, UpdateCoachRequest } from '../../../types';
import { TimezoneSelect } from '../../../components/common';

interface EditLocationTimezoneModalProps {
  opened: boolean;
  onClose: () => void;
  coach: Coach;
  onSaved: (coach: Coach) => void;
}

export function EditLocationTimezoneModal({
  opened,
  onClose,
  coach,
  onSaved,
}: EditLocationTimezoneModalProps) {
  const form = useForm<UpdateCoachRequest>({
    initialValues: {
      location: '',
      home_base_timezone: '',
      timezone_preference: '',
    },
    validate: {
      location: (value) => (value && value.trim() ? null : 'Location is required'),
      home_base_timezone: (value) =>
        value && value.trim() ? null : 'Home base timezone is required',
    },
  });

  useEffect(() => {
    if (opened && coach) {
      form.setValues({
        location: coach.location || '',
        home_base_timezone: coach.home_base_timezone || '',
        timezone_preference: coach.timezone_preference || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.setValues is stable, only re-run when modal opens with new coach data
  }, [opened, coach]);

  const handleSubmit = async (values: UpdateCoachRequest) => {
    try {
      const updatedCoach = await coachesApi.update(coach.id, values);
      notifications.show({
        title: 'Success',
        message: 'Location & timezone updated successfully',
        color: 'green',
      });
      onSaved(updatedCoach);
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update location & timezone',
        color: 'red',
      });
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Edit Location & Timezone" size="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Location"
            placeholder="City, State, Country"
            required
            description="e.g., Chicago, IL, USA"
            {...form.getInputProps('location')}
          />
          <TimezoneSelect
            label="Home Base Timezone"
            placeholder="Select timezone"
            required
            {...form.getInputProps('home_base_timezone')}
          />
          <TimezoneSelect
            label="Current Timezone Preference"
            placeholder="Select timezone"
            clearable
            {...form.getInputProps('timezone_preference')}
          />
          <Group justify="flex-end" className="modal-footer">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
